import { useCallback } from 'react';
import api from '../../../services/api';
import { useSnackbar } from '../../../components/common/Snackbar';

/**
 * Hook to manage project form submission for Projects page
 * @param {Object} params - Hook parameters
 * @param {Object} params.formData - Form data object
 * @param {Function} params.fetchProjects - Function to refetch projects
 * @param {Function} params.handleCloseDialog - Handler to close dialog
 * @param {Function} params.setSubmitLoading - Setter for submit loading state
 * @returns {Function} Submit handler function
 */
export const useProjectsPageForm = ({
  formData,
  fetchProjects,
  handleCloseDialog,
  setSubmitLoading,
}) => {
  const { showSnackbar } = useSnackbar();

  const handleSubmit = useCallback(async () => {
    // Validate required fields
    if (!formData.name || !formData.code || !formData.region || !formData.status || !formData.start_date || !formData.end_date) {
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    setSubmitLoading(true);
    try {
      const payload = {
        name: formData.name,
        code: formData.code,
        region: formData.region,
        description: formData.description,
        status: formData.status,
        startDate: formData.start_date,
        endDate: formData.end_date,
      };

      await api.post('/projects', payload);
      
      showSnackbar('Project created successfully!', 'success');
      handleCloseDialog();
      fetchProjects(); // Refresh the list
    } catch (error) {
      // console.error('Error creating project:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create project. Please try again.';
      showSnackbar(errorMessage, 'error');
    } finally {
      setSubmitLoading(false);
    }
  }, [formData, fetchProjects, handleCloseDialog, setSubmitLoading, showSnackbar]);

  return {
    handleSubmit,
  };
};
